import { useEffect, useMemo, useState } from 'react';

import {
  useAggregatorRunDetail,
} from '../../hooks/usePipelineInsights';
import {
  usePromptInvocation,
} from '../../hooks/usePrompts';
import {
  useBackwardTrace,
  useBackwardTraceRun,
  useForwardTrace,
  useTraceSearch,
} from '../../hooks/useTrace';
import type {
  BackwardTraceResponse,
  ForwardTraceResponse,
  TraceAggregatorRunForDeal,
  TraceFieldProposal,
  TraceRunEvent,
  TraceSearchMode,
} from '../../types/trace';
import styles from './TracePage.module.css';

const MODE_OPTIONS: { value: TraceSearchMode; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'signal_id', label: 'Signal id' },
  { value: 'deal_key', label: 'Deal id / UUID' },
  { value: 'affinity_opp_id', label: 'Affinity opp id' },
  { value: 'subject', label: 'Subject' },
  { value: 'sender', label: 'Sender' },
  { value: 'deal_name', label: 'Deal name' },
];

type Selection =
  | { kind: 'forward'; signalId: number }
  | { kind: 'backward'; dealKind: 'candidate' | 'affinity_opp'; dealKey: string }
  | null;

function readUrlSelection(): Selection {
  const sp = new URLSearchParams(window.location.search);
  const sig = sp.get('signal');
  if (sig && /^\d+$/.test(sig)) return { kind: 'forward', signalId: parseInt(sig, 10) };
  const dk = sp.get('deal_kind');
  const key = sp.get('deal_key');
  if (dk && key && (dk === 'candidate' || dk === 'affinity_opp')) {
    return { kind: 'backward', dealKind: dk, dealKey: key };
  }
  return null;
}

export function TracePage() {
  const initialSelection = useMemo(() => readUrlSelection(), []);
  // Seed the search input from the deep-link URL so the user sees what they
  // traced into. Empty string when there's no URL params.
  const initialQuery =
    initialSelection?.kind === 'forward'
      ? String(initialSelection.signalId)
      : initialSelection?.kind === 'backward'
      ? initialSelection.dealKey
      : '';
  const [query, setQuery] = useState(initialQuery);
  const [mode, setMode] = useState<TraceSearchMode>('auto');
  const [submitted, setSubmitted] = useState(false);
  const [selection, setSelection] = useState<Selection>(initialSelection);

  const search = useTraceSearch(query, mode, submitted);

  useEffect(() => {
    setSubmitted(false);
  }, [query, mode]);

  // Sync the URL when selection changes (so clicking a hit makes the page
  // shareable + reload-safe).
  useEffect(() => {
    if (selection === null) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('signal');
    url.searchParams.delete('deal_kind');
    url.searchParams.delete('deal_key');
    if (selection.kind === 'forward') {
      url.searchParams.set('signal', String(selection.signalId));
    } else {
      url.searchParams.set('deal_kind', selection.dealKind);
      url.searchParams.set('deal_key', selection.dealKey);
    }
    window.history.replaceState({}, '', url.toString());
  }, [selection]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1 className={styles.title}>Trace</h1>
        <span className={styles.subtitle}>
          Phase B forensic view — admin only
        </span>
      </div>

      <form className={styles.searchBar} onSubmit={onSubmit}>
        <input
          className={styles.searchInput}
          placeholder="Search subject / sender / signal-# / candidate-uuid / opp-id …"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className={styles.modeSelect}
          value={mode}
          onChange={(e) => setMode(e.target.value as TraceSearchMode)}
        >
          {MODE_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <button type="submit" className={styles.searchButton}>Search</button>
      </form>

      <div className={styles.body}>
        <aside className={styles.searchResults}>
          {submitted && search.isLoading && <div className={styles.empty}>Loading…</div>}
          {submitted && !search.isLoading && search.data && (
            <>
              <div className={styles.searchMode}>
                mode: {search.data.mode} · {search.data.hits.length} hit{search.data.hits.length === 1 ? '' : 's'}
              </div>
              {search.data.hits.length === 0 && (
                <div className={styles.empty}>(no matches)</div>
              )}
              {search.data.hits.map((h) => (
                <button
                  key={`${h.kind}-${h.id}`}
                  className={styles.hitRow}
                  onClick={() => {
                    if (h.kind === 'signal') {
                      setSelection({ kind: 'forward', signalId: typeof h.id === 'number' ? h.id : parseInt(String(h.id), 10) });
                    } else if (h.kind === 'candidate') {
                      setSelection({ kind: 'backward', dealKind: 'candidate', dealKey: String(h.id) });
                    } else if (h.kind === 'affinity_opp') {
                      setSelection({ kind: 'backward', dealKind: 'affinity_opp', dealKey: String(h.id) });
                    }
                  }}
                >
                  <div className={styles.hitTop}>
                    <span className={`${styles.kindTag} ${styles[`kind_${h.kind}`]}`}>{h.kind}</span>
                    {h.badge && (
                      <span className={`${styles.kindBadge} ${h.badge === 'NEW_DEAL' ? styles.badgeNewDeal : styles.badgeFieldUpdate}`}>
                        {h.badge}
                      </span>
                    )}
                  </div>
                  <div className={styles.hitDisplay}>{h.display}</div>
                  {h.last_seen_at && (
                    <div className={styles.hitMeta}>{formatRel(h.last_seen_at)}</div>
                  )}
                </button>
              ))}
            </>
          )}
          {!submitted && !selection && (
            <div className={styles.empty}>
              Enter a query above to search the pipeline.
            </div>
          )}
        </aside>

        <section className={styles.detail}>
          {selection === null && (
            <div className={styles.empty}>
              Select a search hit to render its trace.
            </div>
          )}
          {selection?.kind === 'forward' && (
            <ForwardTracePane signalId={selection.signalId} />
          )}
          {selection?.kind === 'backward' && (
            <BackwardTracePane
              dealKind={selection.dealKind}
              dealKey={selection.dealKey}
            />
          )}
        </section>
      </div>
    </div>
  );
}

// ===========================================================================
// Forward trace pane (single-column timeline)
// ===========================================================================

function ForwardTracePane({ signalId }: { signalId: number }) {
  const q = useForwardTrace(signalId);
  if (q.isLoading) {
    return <div className={styles.empty}>Loading forward trace…</div>;
  }
  if (q.error) {
    return (
      <div className={styles.errorBox}>
        <strong>Failed to load forward trace.</strong>
        <div>{(q.error as Error).message || String(q.error)}</div>
        <div className={styles.errorHint}>
          Signal #{signalId} may not exist or the trace endpoint hit an error.
        </div>
      </div>
    );
  }
  if (!q.data) {
    return <div className={styles.empty}>(no data)</div>;
  }
  return <ForwardTimeline data={q.data} compact={false} />;
}

export function ForwardTimeline({ data, compact }: { data: ForwardTraceResponse; compact: boolean }) {
  const { signal, pre_filter, resolver_pre, classifier, resolver_post,
          events, candidates_touched, affinity_opps_touched,
          phase_a_suggestion, aggregator_runs, deal_suggestions, pushes } = data;
  return (
    <div className={styles.timeline}>
      {!compact && <SignalCard signal={signal} />}
      <PreFilterCard pre={pre_filter} />
      {resolver_pre && <ResolverCard label="Resolver (pre-classifier)" r={resolver_pre} />}
      {classifier && <ClassifierCard c={classifier} />}
      {resolver_post && resolver_post.fired && <ResolverPostCard r={resolver_post} />}
      {events.length > 0 && (
        <div className={styles.cardGroup}>
          {events.map((e) => (
            <EventCard key={e.id} e={e} />
          ))}
        </div>
      )}
      {candidates_touched.map((c) => (
        <CandidateCard key={c.uuid} c={c} />
      ))}
      {affinity_opps_touched.map((o) => (
        <AffinityOppCard key={o.opp_id} o={o} />
      ))}
      {phase_a_suggestion && <PhaseACard pa={phase_a_suggestion} />}
      {!compact && aggregator_runs.map((r) => (
        <AggregatorRunCard key={r.run_id} run={r} />
      ))}
      {!compact && deal_suggestions.map((s) => (
        <DealSuggestionCard key={s.id} s={s} />
      ))}
      {!compact && pushes.map((p, i) => (
        <PushCard key={i} p={p} />
      ))}
    </div>
  );
}

// ===========================================================================
// Backward trace pane (3-column layout)
// ===========================================================================

export function BackwardTracePane({
  dealKind, dealKey,
}: { dealKind: 'candidate' | 'affinity_opp'; dealKey: string }) {
  const [fieldFilter, setFieldFilter] = useState<string | null>(null);
  const [hidePhaseA, setHidePhaseA] = useState(false);
  const [hideNoise, setHideNoise] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [selectedEventSignalId, setSelectedEventSignalId] = useState<number | null>(null);

  const q = useBackwardTrace(dealKind, dealKey, fieldFilter);
  const data = q.data;

  // Auto-select the active run on first load.
  useEffect(() => {
    if (data && selectedRunId === null) {
      const active = data.aggregator_runs.find((r) => r.is_active);
      if (active) setSelectedRunId(active.run_id);
    }
  }, [data, selectedRunId]);

  // Reset selected event when the field filter changes.
  useEffect(() => {
    setSelectedEventSignalId(null);
  }, [fieldFilter]);

  if (q.isLoading) {
    return <div className={styles.empty}>Loading backward trace…</div>;
  }
  if (q.error) {
    return (
      <div className={styles.errorBox}>
        <strong>Failed to load backward trace.</strong>
        <div>{(q.error as Error).message || String(q.error)}</div>
        <div className={styles.errorHint}>
          {dealKind} {dealKey} may not exist, or the trace endpoint hit an error.
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className={styles.empty}>(no data)</div>;
  }

  return (
    <div className={styles.backwardLayout}>
      <div className={styles.col1}>
        <DealContextPanel
          data={data}
          fieldFilter={fieldFilter}
          onFieldFilterChange={setFieldFilter}
          selectedRunId={selectedRunId}
          onSelectRun={setSelectedRunId}
        />
      </div>
      <div className={styles.col2}>
        <EventListPanel
          dealKind={dealKind}
          dealKey={dealKey}
          data={data}
          selectedRunId={selectedRunId}
          hidePhaseA={hidePhaseA}
          hideNoise={hideNoise}
          fieldFilter={fieldFilter}
          onHidePhaseAChange={setHidePhaseA}
          onHideNoiseChange={setHideNoise}
          selectedEventSignalId={selectedEventSignalId}
          onSelectEvent={setSelectedEventSignalId}
        />
      </div>
      <div className={styles.col3}>
        {selectedEventSignalId === null ? (
          <div className={styles.empty}>← select an event to drill in</div>
        ) : (
          <ForwardTracePaneCompact signalId={selectedEventSignalId} />
        )}
      </div>
    </div>
  );
}

function ForwardTracePaneCompact({ signalId }: { signalId: number }) {
  const q = useForwardTrace(signalId);
  if (q.isLoading) {
    return <div className={styles.empty}>Loading…</div>;
  }
  if (q.error) {
    return (
      <div className={styles.errorBox}>
        <strong>Failed to load.</strong>
        <div>{(q.error as Error).message || String(q.error)}</div>
      </div>
    );
  }
  if (!q.data) {
    return <div className={styles.empty}>(no data)</div>;
  }
  return <ForwardTimeline data={q.data} compact />;
}

function DealContextPanel({
  data, fieldFilter, onFieldFilterChange,
  selectedRunId, onSelectRun,
}: {
  data: BackwardTraceResponse;
  fieldFilter: string | null;
  onFieldFilterChange: (f: string | null) => void;
  selectedRunId: number | null;
  onSelectRun: (id: number) => void;
}) {
  const { deal, latest_suggestion, aggregator_runs, phase_a_linkage } = data;
  return (
    <>
      <div className={styles.cardDeal}>
        <div className={styles.cardHeader}>
          <span className={styles.iconBadge}>{deal.kind === 'candidate' ? '🆕' : '🏢'}</span>
          <span className={styles.cardTitle}>{deal.display_name}</span>
          <a
            href={`/deals?key=${encodeURIComponent(deal.key)}`}
            className={styles.openInDealsChip}
            title="Open this deal in the Deals tab"
          >
            Open in Deals →
          </a>
        </div>
        <div className={styles.cardMeta}>
          {deal.stage || deal.status}
          {deal.codename && ` · ${deal.codename}`}
        </div>
        {deal.promoted_from_candidate_uuid && (
          <div className={styles.note}>
            ↳ promoted from candidate {deal.promoted_from_candidate_uuid.slice(0, 8)}…
          </div>
        )}
      </div>

      {latest_suggestion && (
        <div className={styles.section}>
          <div className={styles.sectionLabelRow}>
            <div className={styles.sectionLabel}>Latest aggregator suggestion</div>
            <div className={styles.sectionCount}>
              #{latest_suggestion.id} · {latest_suggestion.fields.length} field{latest_suggestion.fields.length === 1 ? '' : 's'}
            </div>
          </div>
          {latest_suggestion.health_flags.length > 0 && (
            <div className={styles.fieldSummary}>
              flags: {latest_suggestion.health_flags.join(', ')}
            </div>
          )}
          {latest_suggestion.fields.length === 0 && (
            <div className={styles.empty}>(no field proposals)</div>
          )}
          {latest_suggestion.fields.map((f) => (
            <FieldProposalRow
              key={f.field_name}
              field={f}
              active={fieldFilter === f.field_name}
              onClick={() => onFieldFilterChange(fieldFilter === f.field_name ? null : f.field_name)}
            />
          ))}
          {fieldFilter && (
            <button className={styles.clearFilter} onClick={() => onFieldFilterChange(null)}>
              clear filter
            </button>
          )}
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionLabel}>Aggregator history</div>
        {aggregator_runs.length === 0 && (
          <div className={styles.empty}>(no runs yet)</div>
        )}
        {aggregator_runs.map((r) => (
          <button
            key={r.run_id}
            className={`${styles.runRow} ${selectedRunId === r.run_id ? styles.runRowActive : ''}`}
            onClick={() => onSelectRun(r.run_id)}
          >
            <div className={styles.runRowTop}>
              <span className={styles.runId}>run #{r.run_id}</span>
              {r.is_active && <span className={styles.activeChip}>active</span>}
              <span className={styles.runStatus}>{r.status}</span>
            </div>
            <div className={styles.runMeta}>
              {formatRel(r.ran_at)} · {r.events_processed_count} events
              {r.prompt_version_tag && ` · ${r.prompt_version_tag}`}
            </div>
          </button>
        ))}
      </div>

      {phase_a_linkage.count > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Phase A linkage</div>
          <div className={styles.phaseASummary}>
            {phase_a_linkage.count} suggestion{phase_a_linkage.count === 1 ? '' : 's'}
            {' — '}
            {Object.entries(phase_a_linkage.by_status).map(([s, n], i, arr) => (
              <span key={s}>
                {n} {s}{i < arr.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function FieldProposalRow({
  field, active, onClick,
}: {
  field: TraceFieldProposal;
  active: boolean;
  onClick: () => void;
}) {
  const evidenceLabel = field.evidence_event_ids.length > 0
    ? `evidence #${field.evidence_event_ids.join(', #')}`
    : 'no linked evidence';
  const hasPrior = field.prior_value !== null && field.prior_value !== undefined;
  return (
    <button
      type="button"
      className={`${styles.fieldRow} ${active ? styles.fieldRowActive : ''}`}
      onClick={onClick}
    >
      <div className={styles.fieldTop}>
        <div className={styles.fieldName}>{field.field_name}</div>
        {field.confidence && (
          <span className={styles.fieldConfidence}>{field.confidence}</span>
        )}
      </div>
      <div className={styles.fieldValueLine}>
        <span className={styles.fieldValueLabel}>proposed</span>
        <span className={styles.fieldValue}>{formatValue(field.proposed_value)}</span>
      </div>
      {hasPrior && (
        <div className={styles.fieldValueLine}>
          <span className={styles.fieldValueLabel}>prior</span>
          <span className={styles.fieldPriorValue}>{formatValue(field.prior_value)}</span>
        </div>
      )}
      <div className={styles.fieldReason}>
        {field.reason || 'No reason captured by the aggregator.'}
      </div>
      <div className={styles.fieldMeta}>
        {field.decision || 'pending'} · {evidenceLabel}
        {field.user_set && ' · user-set'}
        {field.dismissed && ' · dismissed'}
      </div>
    </button>
  );
}

function EventListPanel({
  dealKind, dealKey, data, selectedRunId,
  hidePhaseA, hideNoise, fieldFilter,
  onHidePhaseAChange, onHideNoiseChange,
  selectedEventSignalId, onSelectEvent,
}: {
  dealKind: 'candidate' | 'affinity_opp';
  dealKey: string;
  data: BackwardTraceResponse;
  selectedRunId: number | null;
  hidePhaseA: boolean;
  hideNoise: boolean;
  fieldFilter: string | null;
  onHidePhaseAChange: (v: boolean) => void;
  onHideNoiseChange: (v: boolean) => void;
  selectedEventSignalId: number | null;
  onSelectEvent: (sid: number | null) => void;
}) {
  const [resultRunId, setResultRunId] = useState<number | null>(null);
  const isActive = selectedRunId === data.aggregator_runs.find((r) => r.is_active)?.run_id;
  const runQuery = useBackwardTraceRun(
    dealKind, dealKey,
    isActive ? null : selectedRunId,
  );
  const events: TraceRunEvent[] = isActive
    ? data.active_run_events
    : (runQuery.data?.events ?? []);
  const run: TraceAggregatorRunForDeal | undefined =
    data.aggregator_runs.find((r) => r.run_id === selectedRunId);

  const visible = useMemo(() => {
    return events.filter((e) => {
      if (hideNoise && (e.summary.is_noise || e.summary.is_hard_filter_excluded)) {
        return false;
      }
      // Phase A cards are interleaved by phase_a_suggestion_id presence;
      // hide-Phase-A only removes that visual badge, not the event row.
      return true;
    });
  }, [events, hideNoise]);

  return (
    <>
      <div className={styles.col2Header}>
        <div className={styles.col2Title}>
          {run ? `Aggregator run #${run.run_id}` : 'No run selected'}
          {run?.is_active && <span className={styles.activeChip} style={{ marginLeft: 6 }}>active</span>}
        </div>
        {run?.prompt_version_tag && (
          <div className={styles.col2Meta}>
            prompt v{run.prompt_version_tag}
            {' · '}
            tokens {run.tokens.input}/{run.tokens.output}
            {' · $'}{run.cost_usd.toFixed(3)}
          </div>
        )}
        {run && (
          <button
            type="button"
            className={styles.resultButton}
            onClick={() => setResultRunId(run.run_id)}
          >
            View result
          </button>
        )}
        {fieldFilter && (
          <div className={styles.filterChip}>
            filter: {fieldFilter} ({visible.length} evidence event{visible.length === 1 ? '' : 's'})
          </div>
        )}
      </div>

      <div className={styles.col2Filters}>
        <label className={styles.toggle}>
          <input type="checkbox" checked={hidePhaseA} onChange={(e) => onHidePhaseAChange(e.target.checked)} />
          <span>Hide Phase A</span>
        </label>
        <label className={styles.toggle}>
          <input type="checkbox" checked={hideNoise} onChange={(e) => onHideNoiseChange(e.target.checked)} />
          <span>Hide noise/excluded</span>
        </label>
      </div>

      {!isActive && runQuery.isLoading && <div className={styles.empty}>Loading run events…</div>}
      {visible.length === 0 && !runQuery.isLoading && (
        <div className={styles.empty}>(no events)</div>
      )}

      <div className={styles.eventList}>
        {visible.map((e) => {
          const isPhaseA = e.phase_a_suggestion_id !== null;
          const isSelected = selectedEventSignalId === e.signal_id;
          return (
            <button
              key={e.event_log_id}
              className={`${styles.eventRow} ${isSelected ? styles.eventRowSelected : ''} ${
                isPhaseA && !hidePhaseA ? styles.eventRowPhaseA : ''
              }`}
              onClick={() => onSelectEvent(e.signal_id)}
              disabled={e.signal_id === null}
            >
              <div className={styles.eventTop}>
                <span className={styles.eventIcon}>
                  {e.event_type === 'meeting' ? '📅' : '📧'}
                </span>
                <span className={styles.eventId}>#{e.event_log_id}</span>
                {e.pipeline && e.pipeline !== 'deal' && (
                  <span className={styles.pipelineBadge}>{e.pipeline}</span>
                )}
                {e.triggered_by && (
                  <span className={styles.triggerLabel}>
                    {e.triggered_by === 'pipeline_auto' ? 'auto' : e.triggered_by === 'human_forward' ? 'human' : e.triggered_by}
                  </span>
                )}
                {isPhaseA && !hidePhaseA && (
                  <span className={styles.phaseABadge}>PH.A</span>
                )}
                {e.summary.classifier_disposition && (
                  <span className={`${styles.dispoBadge} ${styles[`dispo_${e.summary.classifier_disposition}`]}`}>
                    {e.summary.classifier_disposition}
                  </span>
                )}
                {e.summary.is_hard_filter_excluded && (
                  <span className={styles.excludeBadge}>⊘ ops</span>
                )}
              </div>
              <div className={styles.eventSubject}>
                {e.summary.subject || '(no subject)'}
              </div>
              <div className={styles.eventMeta}>
                {e.summary.sender || '(no sender)'} · {formatRel(e.occurred_at)}
              </div>
            </button>
          );
        })}
      </div>
      {resultRunId !== null && (
        <LlmResultModal
          title={`Aggregator run #${resultRunId}`}
          kindLabel="Aggregator"
          aggregatorRunId={resultRunId}
          promptInvocationId={
            data.aggregator_runs.find((r) => r.run_id === resultRunId)?.prompt_invocation_id ?? null
          }
          metadata={
            data.aggregator_runs.find((r) => r.run_id === resultRunId) ?? null
          }
          onClose={() => setResultRunId(null)}
        />
      )}
    </>
  );
}

// ===========================================================================
// LLM result modal
// ===========================================================================

type LlmModalTab = 'result' | 'prompt' | 'raw' | 'metadata';

function LlmResultModal({
  title,
  kindLabel,
  result,
  aggregatorRunId,
  promptInvocationId,
  metadata,
  onClose,
}: {
  title: string;
  kindLabel: string;
  result?: unknown;
  aggregatorRunId?: number | null;
  promptInvocationId?: number | null;
  metadata?: unknown;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<LlmModalTab>('result');
  const runDetail = useAggregatorRunDetail(aggregatorRunId ?? null);
  const effectiveInvocationId =
    promptInvocationId ?? runDetail.data?.run.prompt_invocation_id ?? null;
  const invocation = usePromptInvocation(effectiveInvocationId);

  useEffect(() => {
    setTab('result');
  }, [aggregatorRunId, promptInvocationId]);

  const metaPayload = {
    source: kindLabel,
    prompt_invocation_id: effectiveInvocationId,
    card_metadata: metadata ?? null,
    aggregator_run: runDetail.data?.run ?? null,
    prompt_invocation: invocation.data
      ? {
          id: invocation.data.id,
          purpose: invocation.data.purpose,
          version_tag: invocation.data.version_tag,
          model: invocation.data.model,
          invoked_at: invocation.data.invoked_at,
          input_tokens: invocation.data.input_tokens,
          output_tokens: invocation.data.output_tokens,
          cost_usd: invocation.data.cost_usd,
        }
      : null,
  };

  return (
    <div
      className={styles.modalBackdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section className={styles.modalPanel} role="dialog" aria-modal="true">
        <header className={styles.modalHeader}>
          <div>
            <div className={styles.modalKind}>{kindLabel}</div>
            <h3 className={styles.modalTitle}>{title}</h3>
          </div>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label="Close result modal"
          >
            ×
          </button>
        </header>

        <div className={styles.modalTabs}>
          {(['result', 'prompt', 'raw', 'metadata'] as LlmModalTab[]).map((t) => (
            <button
              key={t}
              type="button"
              className={`${styles.modalTab} ${tab === t ? styles.modalTabActive : ''}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className={styles.modalBody}>
          {tab === 'result' && (
            aggregatorRunId ? (
              <AggregatorResultBody
                loading={runDetail.isLoading}
                error={runDetail.error}
                detail={runDetail.data}
              />
            ) : (
              <JsonBlock value={result ?? null} />
            )
          )}

          {tab === 'prompt' && (
            <PromptBody
              loading={invocation.isLoading}
              error={invocation.error}
              system={invocation.data?.rendered_system ?? null}
              user={invocation.data?.rendered_user ?? null}
              invocationId={effectiveInvocationId}
            />
          )}

          {tab === 'raw' && (
            <PromptRawBody
              loading={invocation.isLoading}
              error={invocation.error}
              raw={invocation.data?.raw_response ?? null}
              invocationId={effectiveInvocationId}
            />
          )}

          {tab === 'metadata' && (
            <JsonBlock value={metaPayload} />
          )}
        </div>
      </section>
    </div>
  );
}

function AggregatorResultBody({
  loading,
  error,
  detail,
}: {
  loading: boolean;
  error: unknown;
  detail: ReturnType<typeof useAggregatorRunDetail>['data'];
}) {
  if (loading) return <div className={styles.empty}>Loading result…</div>;
  if (error) {
    return (
      <div className={styles.errorBox}>
        Failed: {(error as Error).message || String(error)}
      </div>
    );
  }
  if (!detail) return <div className={styles.empty}>(no result payload)</div>;

  const fields = detail.suggestion?.fields ?? {};
  const fieldEntries = Object.entries(fields);
  return (
    <div className={styles.modalStack}>
      <div className={styles.modalMetaGrid}>
        <div>
          <span>status</span>
          <strong>{detail.run.status}</strong>
        </div>
        <div>
          <span>events</span>
          <strong>{detail.run.events_processed}</strong>
        </div>
        <div>
          <span>tokens</span>
          <strong>{detail.run.input_tokens}/{detail.run.output_tokens}</strong>
        </div>
        <div>
          <span>cost</span>
          <strong>${detail.run.cost_usd.toFixed(3)}</strong>
        </div>
      </div>

      {detail.run.error && (
        <div className={styles.modalErrorText}>{detail.run.error}</div>
      )}

      {detail.suggestion?.synthesized_description && (
        <section className={styles.modalSection}>
          <h4>description</h4>
          <p>{detail.suggestion.synthesized_description}</p>
        </section>
      )}

      <section className={styles.modalSection}>
        <h4>fields ({fieldEntries.length})</h4>
        {fieldEntries.length === 0 ? (
          <div className={styles.empty}>(no field output)</div>
        ) : (
          <div className={styles.modalFieldList}>
            {fieldEntries.map(([name, value]) => (
              <div key={name} className={styles.modalFieldRow}>
                <div className={styles.modalFieldName}>{name}</div>
                <pre>{formatJson(value)}</pre>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className={styles.modalSection}>
        <h4>full suggestion payload</h4>
        <JsonBlock value={detail.suggestion} />
      </section>
    </div>
  );
}

function PromptBody({
  loading,
  error,
  system,
  user,
  invocationId,
}: {
  loading: boolean;
  error: unknown;
  system: string | null;
  user: string | null;
  invocationId: number | null;
}) {
  if (!invocationId) return <div className={styles.empty}>(no prompt invocation recorded)</div>;
  if (loading) return <div className={styles.empty}>Loading prompt…</div>;
  if (error) {
    return (
      <div className={styles.errorBox}>
        Failed: {(error as Error).message || String(error)}
      </div>
    );
  }
  return (
    <div className={styles.modalStack}>
      <section className={styles.modalSection}>
        <h4>rendered system</h4>
        <pre className={styles.modalCode}>{system || ''}</pre>
      </section>
      <section className={styles.modalSection}>
        <h4>rendered user</h4>
        <pre className={styles.modalCode}>{user || ''}</pre>
      </section>
    </div>
  );
}

function PromptRawBody({
  loading,
  error,
  raw,
  invocationId,
}: {
  loading: boolean;
  error: unknown;
  raw: string | null;
  invocationId: number | null;
}) {
  if (!invocationId) return <div className={styles.empty}>(no prompt invocation recorded)</div>;
  if (loading) return <div className={styles.empty}>Loading raw response…</div>;
  if (error) {
    return (
      <div className={styles.errorBox}>
        Failed: {(error as Error).message || String(error)}
      </div>
    );
  }
  return <pre className={styles.modalCode}>{raw || ''}</pre>;
}

function JsonBlock({ value }: { value: unknown }) {
  return <pre className={styles.modalCode}>{formatJson(value)}</pre>;
}

// ===========================================================================
// Trace cards (compact inline; one component per type)
// ===========================================================================

function SignalCard({ signal }: { signal: ForwardTraceResponse['signal'] }) {
  return (
    <div className={`${styles.card} ${styles.cardSignal}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>{signal.source === 'calendar' ? '📅' : '📧'}</span>
        <span className={styles.cardTitle}>signal #{signal.id}</span>
      </div>
      <div className={styles.cardLine}>
        from <strong>{signal.sender || '—'}</strong> ·
        received {formatRel(signal.received_at)}
      </div>
      <div className={styles.cardLine}>
        {signal.subject || '(no subject)'}
      </div>
      {signal.body_excerpt && (
        <details className={styles.details}>
          <summary>view body excerpt</summary>
          <pre className={styles.pre}>{signal.body_excerpt}</pre>
        </details>
      )}
    </div>
  );
}

function PreFilterCard({ pre }: { pre: ForwardTraceResponse['pre_filter'] }) {
  return (
    <div className={`${styles.card} ${pre.outcome === 'rejected' ? styles.cardPreFilterRejected : styles.cardPreFilter}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>🛡</span>
        <span className={styles.cardTitle}>Pre-filter — {pre.outcome}</span>
      </div>
      <div className={styles.cardLine}>
        score: {pre.score} · reasons: {pre.reasons.length > 0 ? pre.reasons.join(', ') : '(none)'}
      </div>
      {pre.rejection_reason && (
        <div className={styles.cardLine}>rejection: {pre.rejection_reason}</div>
      )}
    </div>
  );
}

function ResolverCard({ label, r }: { label: string; r: ForwardTraceResponse['resolver_pre'] }) {
  if (!r) return null;
  return (
    <div className={`${styles.card} ${styles.cardResolver}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>🎯</span>
        <span className={styles.cardTitle}>{label}</span>
      </div>
      <div className={styles.cardLine}>
        method: <strong>{r.method}</strong> · confidence: {r.confidence} · score: {r.score}
      </div>
      {r.bound_to.kind && (
        <div className={styles.cardLine}>
          bound to: {r.bound_to.kind}{' '}
          <strong>{r.bound_to.display || r.bound_to.key}</strong>
        </div>
      )}
      {r.portco_signal !== 'none' && (
        <div className={styles.cardLine}>portco signal: {r.portco_signal}</div>
      )}
      {r.reasons.length > 0 && (
        <details className={styles.details}>
          <summary>reasons ({r.reasons.length})</summary>
          <ul className={styles.list}>
            {r.reasons.map((reason, i) => <li key={i}>{reason}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

function ResolverPostCard({ r }: { r: ForwardTraceResponse['resolver_post'] }) {
  if (!r || !r.fired) return null;
  return (
    <div className={`${styles.card} ${styles.cardResolver}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>🎯</span>
        <span className={styles.cardTitle}>Resolver (post-classifier match)</span>
      </div>
      <div className={styles.cardLine}>
        method: <strong>{r.method}</strong> · score: {r.score}
      </div>
      {r.bound_to.kind && (
        <div className={styles.cardLine}>
          bound to: {r.bound_to.kind} <strong>{r.bound_to.display || r.bound_to.key}</strong>
        </div>
      )}
    </div>
  );
}

function ClassifierCard({ c }: { c: ForwardTraceResponse['classifier'] }) {
  const [modalOpen, setModalOpen] = useState(false);
  if (!c) return null;
  if (!c.invoked) {
    return (
      <div className={`${styles.card} ${styles.cardSkipped}`}>
        <div className={styles.cardHeader}>
          <span className={styles.iconBadge}>🧠</span>
          <span className={styles.cardTitle}>Classifier — skipped</span>
        </div>
        <div className={styles.cardLine}>reason: {c.skip_reason || 'unknown'}</div>
      </div>
    );
  }
  const result = c.result || {};
  return (
    <div className={`${styles.card} ${styles.cardClassifier}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>🧠</span>
        <span className={styles.cardTitle}>Classifier</span>
        <button
          type="button"
          className={styles.resultButton}
          onClick={() => setModalOpen(true)}
        >
          View result
        </button>
        {c.model && <span className={styles.cardMetaInline}>{c.model}</span>}
      </div>
      <div className={styles.cardLine}>
        is_origination_signal: <strong>{String(result.is_origination_signal ?? '—')}</strong>
        {' · '}signal_type: {String(result.signal_type ?? '—')}
        {' · '}confidence: {String(result.confidence ?? '—')}
      </div>
      {(result.company_name || result.project_codename) ? (
        <div className={styles.cardLine}>
          extracted: {result.company_name ? <strong>{String(result.company_name)}</strong> : '—'}
          {result.project_codename ? <> · codename <strong>{String(result.project_codename)}</strong></> : null}
        </div>
      ) : null}
      <div className={styles.cardLine}>
        tokens: {c.tokens.input} in / {c.tokens.output} out · cost: ${c.cost_usd.toFixed(3)}
      </div>
      {c.prompt_invocation_id !== null && (
        <a
          className={styles.linkOut}
          href={`/?tab=prompts&invocation=${c.prompt_invocation_id}`}
        >
          🔍 view prompt invocation #{c.prompt_invocation_id}
        </a>
      )}
      <details className={styles.details}>
        <summary>view full classifier result</summary>
        <pre className={styles.pre}>{JSON.stringify(result, null, 2)}</pre>
      </details>
      {modalOpen && (
        <LlmResultModal
          title="Classifier result"
          kindLabel="Classifier"
          result={result}
          promptInvocationId={c.prompt_invocation_id}
          metadata={{
            model: c.model,
            tokens: c.tokens,
            cost_usd: c.cost_usd,
          }}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

function EventCard({ e }: { e: ForwardTraceResponse['events'][number] }) {
  return (
    <div className={`${styles.card} ${styles.cardEvent}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>🔗</span>
        <span className={styles.cardTitle}>Event #{e.id} → {e.deal_kind} {e.deal_display}</span>
      </div>
      <div className={styles.cardLine}>
        method: {e.resolution_method} · confidence: {e.resolution_confidence}
      </div>
    </div>
  );
}

function CandidateCard({ c }: { c: ForwardTraceResponse['candidates_touched'][number] }) {
  return (
    <div className={`${styles.card} ${styles.cardCandidate}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>🆕</span>
        <span className={styles.cardTitle}>Candidate {c.display_name}</span>
        <span className={styles.cardMetaInline}>{c.status}</span>
        <a
          href={`/deals?key=${encodeURIComponent(c.uuid)}`}
          className={styles.openInDealsChip}
          title="Open this candidate in the Deals tab"
        >
          Open in Deals →
        </a>
      </div>
      <div className={styles.cardLine}>uuid: {c.uuid}</div>
      {c.cluster_keys.length > 0 && (
        <div className={styles.cardLine}>
          cluster keys: {c.cluster_keys.map((k) => `${k.key_type}=${k.key_value}`).join(', ')}
        </div>
      )}
    </div>
  );
}

function AffinityOppCard({ o }: { o: ForwardTraceResponse['affinity_opps_touched'][number] }) {
  return (
    <div className={`${styles.card} ${styles.cardAffinityOpp}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>🏢</span>
        <span className={styles.cardTitle}>Affinity opp #{o.opp_id} — {o.display_name}</span>
        <a
          href={`/deals?key=${encodeURIComponent(String(o.opp_id))}`}
          className={styles.openInDealsChip}
          title="Open this Affinity opp in the Deals tab"
        >
          Open in Deals →
        </a>
      </div>
      <div className={styles.cardLine}>
        needs_aggregation: {String(o.needs_aggregation)}
        {' · '}last_aggregated: {formatRel(o.last_aggregated_at)}
      </div>
    </div>
  );
}

function PhaseACard({ pa }: { pa: ForwardTraceResponse['phase_a_suggestion'] }) {
  if (!pa) return null;
  return (
    <div className={`${styles.card} ${styles.cardPhaseA}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>📝</span>
        <span className={styles.cardTitle}>Phase A suggestion #{pa.id}</span>
        <span className={styles.phaseABadge}>PH.A</span>
      </div>
      <div className={styles.cardLine}>
        status: {pa.status} · signal_type: {pa.signal_type || '—'}
        {pa.matched_opportunity_id && ` · matched opp #${pa.matched_opportunity_id}`}
      </div>
      {pa.dismiss_reason && (
        <div className={styles.cardLine}>dismiss: {pa.dismiss_reason}</div>
      )}
      <div className={styles.cardLine}>
        Historical legacy suggestion retained for trace context.
      </div>
    </div>
  );
}

function AggregatorRunCard({ run }: { run: ForwardTraceResponse['aggregator_runs'][number] }) {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <div className={`${styles.card} ${styles.cardAggregator}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>🤖</span>
        <span className={styles.cardTitle}>Aggregator run #{run.run_id}</span>
        <button
          type="button"
          className={styles.resultButton}
          onClick={() => setModalOpen(true)}
        >
          View result
        </button>
        <span className={styles.cardMetaInline}>{run.status}</span>
      </div>
      <div className={styles.cardLine}>
        ran: {formatRel(run.ran_at)} · {run.events_processed_count} events
        {run.prompt_version_tag && ` · prompt v${run.prompt_version_tag}`}
      </div>
      <div className={styles.cardLine}>
        tokens: {run.tokens.input}/{run.tokens.output} · ${run.cost_usd.toFixed(3)}
      </div>
      {run.prompt_invocation_id !== null && (
        <a className={styles.linkOut} href={`/?tab=prompts&invocation=${run.prompt_invocation_id}`}>
          🔍 view prompt invocation #{run.prompt_invocation_id}
        </a>
      )}
      {modalOpen && (
        <LlmResultModal
          title={`Aggregator run #${run.run_id}`}
          kindLabel="Aggregator"
          aggregatorRunId={run.run_id}
          promptInvocationId={run.prompt_invocation_id}
          metadata={run}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

function DealSuggestionCard({ s }: { s: ForwardTraceResponse['deal_suggestions'][number] }) {
  return (
    <div className={`${styles.card} ${styles.cardDealSuggestion}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>📄</span>
        <span className={styles.cardTitle}>Deal suggestion #{s.id}</span>
      </div>
      <div className={styles.cardLine}>
        on {s.deal_kind} {s.deal_display}
        {' · '}{s.fields_count} field{s.fields_count === 1 ? '' : 's'}
        {' · '}created {formatRel(s.created_at)}
      </div>
      {s.referenced_by_fields.length > 0 && (
        <div className={styles.cardLine}>
          this signal contributed to: {s.referenced_by_fields.join(', ')}
        </div>
      )}
      <a
        className={styles.linkOut}
        href={`/?tab=deals&deal_key=${s.deal_key}`}
      >
        open in Deals tab →
      </a>
    </div>
  );
}

function PushCard({ p }: { p: ForwardTraceResponse['pushes'][number] }) {
  return (
    <div className={`${styles.card} ${styles.cardPush}`}>
      <div className={styles.cardHeader}>
        <span className={styles.iconBadge}>☁</span>
        <span className={styles.cardTitle}>Pushed to Affinity</span>
      </div>
      <div className={styles.cardLine}>
        by {p.by || '—'} · {formatRel(p.pushed_at)}
        {p.affinity_opp_id && ` · opp #${p.affinity_opp_id}`}
      </div>
      <div className={styles.cardLine}>
        fields: {p.fields_pushed.length > 0 ? p.fields_pushed.join(', ') : '(note only)'}
        {p.note_id && ` · note #${p.note_id}`}
      </div>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function formatRel(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length > 80 ? v.slice(0, 80) + '…' : v;
  if (Array.isArray(v)) return `[${v.length} items]`;
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 80);
  return String(v);
}

function formatJson(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
